/**
 * Les deux liens d'impression d'un sujet : l'énoncé et, à part, le corrigé.
 *
 * Demandé par Hedi le 26 septembre 2026 : *« Pour les sujets entiers donnés, je
 * veux que le parent puisse ouvrir un pdf du sujet et un pdf de la correction
 * pour éventuellement imprimer. »* Le PDF sert à **imprimer**, pas à juger : une
 * page A4 par image, dans l'ordre, rien d'autre.
 *
 * Deux documents et jamais un seul : le sujet se pose devant l'enfant, le
 * corrigé reste chez l'adulte. Le serveur ne passe au PDF du sujet que les clés
 * de l'énoncé (règle 2), et le corrigé porte sur chaque page la mention « À
 * garder par l'adulte ».
 *
 * Ce sont des liens et non des appels `call` : le navigateur les ouvre lui-même,
 * avec le cookie de session (règle 8), dans un nouvel onglet où son lecteur sait
 * imprimer — le même choix que pour la feuille d'entraînement.
 *
 * **Vit dans `ui/`** parce que la V1 (`parent/`) et la V2 s'en servent et
 * qu'aucune des deux n'importe l'autre. **La coquille enfant ne s'en sert pas** :
 * les routes sont celles du parent, et un test balaie `src/child/**` pour
 * qu'aucun écran d'enfant ne pointe vers un PDF.
 */

import { buildUrl } from "@/api/client";

/** Ce qu'on imprime : un sujet donné (V2) ou un exercice à images (V1). */
export type AImprimer =
  | { quoi: "sujet"; childId: string; sujetDonneId: string }
  | { quoi: "exercice"; childId: string; exerciseId: string };

/** Les deux adresses. Pures, et exportées pour être testées sans DOM. */
export function adressesDImpression(cible: AImprimer): {
  sujet: string;
  corrige: string;
} {
  if (cible.quoi === "sujet") {
    const params = {
      child_id: cible.childId,
      sujet_donne_id: cible.sujetDonneId,
    };
    return {
      sujet: buildUrl("imprimerLeSujet", { params }),
      corrige: buildUrl("imprimerLeCorrigeDuSujet", { params }),
    };
  }
  const params = { child_id: cible.childId, exercise_id: cible.exerciseId };
  return {
    sujet: buildUrl("imprimerLEnonce", { params }),
    corrige: buildUrl("imprimerLeCorrigeDeLExercice", { params }),
  };
}

export function LiensDImpression({
  cible,
  corrige = true,
}: {
  cible: AImprimer;
  /**
   * Faux pour ne proposer que le sujet — sur l'écran de ce qui sera proposé,
   * l'exercice n'est pas encore fait, et le corrigé s'ouvrirait à côté de
   * l'enfant. Faux aussi quand on sait qu'il n'y a pas d'image de corrigé :
   * le lien répondrait 404.
   */
  corrige?: boolean;
}) {
  const adresses = adressesDImpression(cible);
  return (
    <div className="list__actions">
      <a className="btn btn--quiet" href={adresses.sujet} target="_blank" rel="noreferrer">
        Sujet (PDF)
      </a>
      {corrige ? (
        <a className="btn btn--quiet" href={adresses.corrige} target="_blank" rel="noreferrer">
          Corrigé (PDF)
        </a>
      ) : null}
    </div>
  );
}
